import * as React from 'react';
import { css } from 'styled-components';
import { UitkButtonSize } from 'uitk-react-button';
// Module Context
import { useComponentContext } from 'src/components/common/modules/component-layout/component-context';
import { useBexApi } from '@shared-ui/bex-api-context';
import { useStores } from 'src/utils/useStore';

// Components
import Layout from 'src/components/common/theme/Layout';
import { UitkFigureAspectRatioType } from 'uitk-react-images';
import { FigureComponent, Typo } from 'src/components/common/ui';
import { PrimaryButton, PrimaryButtonProps } from 'src/components/common/ui/button/button';
import EntryForm from 'src/components/common/modules/v1/entry-form/entry-form';
import { EntryFormProps } from 'src/components/common/modules/v1/entry-form/code-split/entry-form-map';
import { checkValue, LightenDarkenColor, isDateExpired } from 'src/components/common/helper';
import { useWindowDimensions } from 'src/components/common/useWindowDimentions';
import { getDeviceImage } from 'src/components/common/theme/component';
import { useLocation } from 'react-router-dom-v5-compat';

const CompetitionEntry: React.FC = () => {
  const {search} = useLocation()
  const searchParam = new URLSearchParams(search)
  const querySignin = searchParam.get("signin");

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    componentProps: { data: { data, defaultSetting }, handleEvent, key, ...rest },
  } = useComponentContext();
  
  const {
    signInTitle,
    signInSubtitle,
    signInDescription,
    signInCtaUrl,
    signInCtaText,
    title,
    description,
    questions,
    module,
    uniquekey,
    cTAText,
    successEntryTitle,  
    successEntryCopy,
    successEntryCtaText,
    successEntryCtaUrl,
    sweepstakesEndCopy,
    competitionEndCopy,
    ctaColor,
    ctaTextColor,
    requireSignIn,
    backgroundColor,
    backgroundImage,
    textColor,
    formElementBackgroundColor,
    formElementPlaceholderTextColor,
    formElementTextColor,
    turnOffReCAPTCHA,
    registerCtaText,
    registerCtaUrl,
    registerCtaColor,
    moduleStyle,
    hideTitleAndDescriptionForEndCopy,
    isQuiz,
    competitionEndDatetime,
    competitionEndMode
  } = data

  const today = new Date();
  const isExpired = checkValue(competitionEndDatetime)?!isDateExpired(today, competitionEndDatetime, false):false;

  const displayTitleAndDescription = hideTitleAndDescriptionForEndCopy === "False" || !checkValue(hideTitleAndDescriptionForEndCopy) ? true : false;
  const { width: windowWidth } = useWindowDimensions();
  const { context } = useBexApi();
  const { context: storeContext } = useStores();
  const expediaUserId = context?.identity?.expUserId;
  const authState = context?.identity?.authState;
  const currentLocation = window?.location?.pathname;
  const host = window?.location?.host;
  const protocol = window?.location?.protocol;
  const isSignInRequired = requireSignIn?.toLowerCase() === 'true' && (authState === "ANONYMOUS" || expediaUserId === '-1');
  const moduleStyleClass = moduleStyle === 'Style-2' ? 'style-two' : '';

  let primaryDarkColor = '';
  if(checkValue(ctaColor)){
    primaryDarkColor = LightenDarkenColor(ctaColor, -0.1);
  };

  let ctaURL = '';
  if (checkValue(signInCtaUrl)) {
    ctaURL = signInCtaUrl;
    ctaURL = `${signInCtaUrl}${encodeURIComponent(`#${uniquekey}`)}`;
    if (!ctaURL.includes('http') && checkValue(host) && checkValue(protocol)) ctaURL = `${protocol}//${host}${ctaURL}`;
  } else if (checkValue(currentLocation)) {
    ctaURL = `/login?ckoflag=0&uurl=${encodeURIComponent(`e3id=redr&rurl=${currentLocation}#${uniquekey}`)}`;
  }

  let windowSize = '';
  if (windowWidth > 920) windowSize = 'desktop';
  else if (windowWidth > 575) windowSize = 'tablet';
  else windowSize = 'mobile';

  // Dynamic style overrides
  const moduleCss = () => {
    return `
      ${checkValue(ctaColor) || checkValue(ctaTextColor) ?
        `
        .competition-entry-button {
          ${checkValue(ctaColor) ? `background-color: ${ctaColor} !important;` : ''} 
          ${checkValue(ctaTextColor) ? `color: ${ctaTextColor} !important;` : ''}
        }
        .competition-entry-button:hover {
          ${checkValue(primaryDarkColor) ? `background-color: ${primaryDarkColor} !important;` : ''}
        }
        ` : ''
      }
      ${checkValue(backgroundImage, true) || checkValue(backgroundColor)
        ? `
        .competition-entry-bg {
          background-color: ${backgroundColor};
          background-position: center !important;
          background-size: cover !important;
          background-repeat: no-repeat;
          background: ${
            checkValue(backgroundImage, true) && checkValue(isQuiz)
              ? `
              linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)),
              url(${getDeviceImage(backgroundImage, windowSize)})
              `
              : checkValue(backgroundImage, true)
              ? `
              linear-gradient(90deg,
                rgba(41, 47, 66, 0) 0%,
                rgba(41, 47, 66, 0.2) 32.29%,
                rgba(41, 49, 66, 0.55) 100%),
              url(${getDeviceImage(backgroundImage, windowSize)})
              `
              : null
          }
        }
        `
        : null
      }
      ${
        checkValue(registerCtaColor)
          ? `
        .competition-entry-register {
          border-color: ${registerCtaColor} !important;
          color: ${registerCtaColor} !important;
        }
        .competition-entry-register:hover {
          border-color: ${LightenDarkenColor(registerCtaColor, -0.1)} !important;
          color: ${LightenDarkenColor(registerCtaColor, -0.1)} !important;
        }
        `
          : ''
      }

      ${checkValue(textColor) ?`.competition-entry .container .competition-entry-wrapper .competition-entry-text h3,p
       {color: ${textColor} !important;}` 
       :`.competition-entry .container .competition-entry-wrapper .competition-entry-text h3,p
       {color: white !important;}`}

      ${checkValue(textColor) ? 
        `.competition-entry-bg .container .competition-entry-wrapper {
          .competition-entry-text,
          .entry-form form label,
          .entry-form form label a,
          .entry-form form label .group-label > span,
          .entry-form form .entry-form-small-text,
          .entry-form form .entry-form-small-text > a,
          h2,
          h3,
          p {
            color: ${textColor} !important;
          }
        }` :  `.entry-form__success {
          h3, p {
            color: white !important;
          }
        }`
      }
    `;
  };

  /* use this to get logged-in user details
  React.useEffect(() => {
    const checkLogin = async () => {
      const data = await bexapistore.fetchMemberWalletQuery(context, 'meso3');
      console.log(data)
    }
    checkLogin();
  }, []);
  */

  const registerButtonProps: PrimaryButtonProps = {
    text: registerCtaText,
    tag: 'a',
    gaTracking: {
      eventCategory: 'Button Click',
      eventAction: 'competition Entry Register',
      eventLabel: registerCtaText,
    },
    exClass: `competition-entry-register`,
    url: registerCtaUrl,
    target: '_self',
    size: UitkButtonSize.LARGE,
  };
  
  const scrollToTop = () => {
    const element = document.getElementById(uniquekey);
    if (element) {
      const headerOffset = 130;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;
      window.scrollTo({
           top: offsetPosition,
           behavior: "smooth"
      });
    }
  }

  const handleEntryFormChange = () => {
    handleEvent();
  }

  const primaryButtonProps: PrimaryButtonProps = {
    text: signInCtaText,
    tag: 'a',
    gaTracking: {
      eventCategory: 'Button Click',
      eventAction: 'competition Entry',
      eventLabel: signInCtaText,
    },
    exClass: `competition-entry-button`,
    url: ctaURL,
    target: '_self',
    size: UitkButtonSize.LARGE,
  };

  const entryFormProps: EntryFormProps = {
    module,
    uniquekey,
    questions,
    primaryColour: defaultSetting?.style?.primaryColour,
    secondaryColour: defaultSetting?.style?.secondaryColour,
    campaign_name: `${defaultSetting?.site?.partner}.${defaultSetting?.site?.templateName}.${module}`,
    cTAText,
    context: storeContext,
    successEntryTitle,
    successEntryCopy,
    successEntryCtaText,
    successEntryCtaUrl,
    sweepstakesEndCopy,
    competitionEndCopy,
    cTATextColor: ctaTextColor,
    cTAColor: ctaColor,
    formElementBackgroundColor,
    textColor,
    formElementPlaceholderTextColor,
    formElementTextColor,
    backgroundImage,
    turnOffReCAPTCHA,
    scrollToTop: scrollToTop,
    onEntryFormChange: handleEntryFormChange,
    isQuiz
  };

  const signIn = () => (
    <>
      <FigureComponent
            exClass='logo'
            ratio={UitkFigureAspectRatioType.R1_1} 
            alt='brand logo' 
            src={`https://a.travel-assets.com/egds/marks/brands/${defaultSetting?.site?.brand}/app.svg`}
      />
      { signInTitle ? <Typo.HTMLText tag="h2" exClass="competition-entry-text competition-entry-title override-heading-font-family">{signInTitle}</Typo.HTMLText> : null }
      { signInSubtitle ? <Typo.HTMLText tag='h3' exClass='competition-entry-text competition-entry-sub-title'>{signInSubtitle}</Typo.HTMLText> : null }
      { signInDescription ? <Typo.HTMLText tag='p' exClass='competition-entry-text competition-entry-content'>{signInDescription}</Typo.HTMLText> : null }
      <div className="competition-entry-cta">
        {(currentLocation || registerCtaUrl) && registerCtaUrl ? <PrimaryButton {...registerButtonProps} /> : null}
        {(currentLocation || ctaURL) && ctaURL ? <PrimaryButton {...primaryButtonProps} /> : null}
      </div>
    </>
  );
  
  const competitionFormQuizzes = () => {
    if(checkValue(questions) && checkValue(competitionEndCopy)){
      if(checkValue(competitionEndDatetime) && !isExpired){
        return <EntryForm {...entryFormProps} />;
      }else{
        return <Typo.HTMLText tag={displayTitleAndDescription ?'p':'h2'} exClass='competition-entry-text'>{competitionEndCopy}</Typo.HTMLText>;  
      }      
    }else{
      if(!isExpired){
        return <EntryForm {...entryFormProps} />;
      }else{
        return <Typo.HTMLText tag={displayTitleAndDescription ?'p':'h2'} exClass='competition-entry-text'>{competitionEndCopy}</Typo.HTMLText>;  
      }
    }
  };
  
  const competitionForm = () => {    
    return(
      <>
        { title && (!checkValue(competitionEndCopy)  || displayTitleAndDescription ) ? <Typo.HTMLText tag='h2' exClass='competition-entry-text competition-entry-title override-heading-font-family'>{title}</Typo.HTMLText> : null }
        { description && (!checkValue(competitionEndCopy)  || displayTitleAndDescription )   ? <Typo.HTMLText tag='p' exClass='competition-entry-text competition-entry-content'>{description}</Typo.HTMLText> : null }
        {competitionFormQuizzes()}
      </>
    )
  };
  
    return (
      <>
        {isExpired && (checkValue(competitionEndMode) && competitionEndMode === 'Hide module') ? null :        
          <Layout 
            css={css`
              ${moduleCss()}
            `}
            className="competition-entry"
            HtmlTag="section"
            id={uniquekey}      
          >
            <div className='competition-entry-bg'>
              <div className={`container ${isSignInRequired ? 'sign-in' : (checkValue(querySignin) && querySignin === '0') ? 'sign-in' : 'competition-form'} ${moduleStyleClass}`}>
                  <div className="competition-entry-wrapper">
                  { 
                  isSignInRequired ? signIn() : (checkValue(querySignin) && querySignin === '0') ? signIn() : competitionForm()
                  }
                  </div>
              </div>
            </div>
          </Layout>
        }
      </>
    );
  // }
};

export default CompetitionEntry;
